import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_BASE = '/api/devtracker';

const severityColors = {
    critical: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
    high: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
    medium: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
    low: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
};

const statusColors = {
    open: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
    investigating: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
    in_progress: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
    resolved: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
    wont_fix: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
    duplicate: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
    active: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
    superseded: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
    archived: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
    documented: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
    implemented: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
    deprecated: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
    under_review: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
    approved: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
    rejected: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
    deferred: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
};

const Badge = ({ children, color }) => (
    <span className={`px-2 py-1 text-xs font-medium rounded-full ${color}`}>
        {children}
    </span>
);

const Card = ({ title, icon, count, color, children }) => (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
        <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <span>{icon}</span> {title}
            </h3>
            <span className={`px-3 py-1 rounded-full text-sm font-bold ${color}`}>{count}</span>
        </div>
        {children}
    </div>
);

const ItemRow = ({ id, title, badges, description, date }) => (
    <div className="border-b border-gray-100 dark:border-gray-700 py-3 last:border-0">
        <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs text-gray-500 dark:text-gray-400 font-mono">#{id}</span>
                    {badges}
                </div>
                <h4 className="text-sm font-medium text-gray-900 dark:text-white mt-1 truncate">{title}</h4>
                {description && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">{description}</p>
                )}
            </div>
            <span className="text-xs text-gray-400 whitespace-nowrap">{new Date(date).toLocaleDateString()}</span>
        </div>
    </div>
);

const DevTrackerPage = () => {
    const [summary, setSummary] = useState(null);
    const [bugs, setBugs] = useState([]);
    const [features, setFeatures] = useState([]);
    const [journal, setJournal] = useState([]);
    const [ideas, setIdeas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('summary');

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const [summaryRes, bugsRes, featuresRes, journalRes, ideasRes] = await Promise.all([
                axios.get(`${API_BASE}/summary/`),
                axios.get(`${API_BASE}/bugs/`),
                axios.get(`${API_BASE}/features/`),
                axios.get(`${API_BASE}/journal/`),
                axios.get(`${API_BASE}/ideas/`),
            ]);
            setSummary(summaryRes.data);
            setBugs(bugsRes.data);
            setFeatures(featuresRes.data);
            setJournal(journalRes.data);
            setIdeas(ideasRes.data);
        } catch (error) {
            console.error('Error fetching devtracker data:', error);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    const tabs = [
        { id: 'summary', label: 'Summary', icon: '📊' },
        { id: 'bugs', label: `Bugs (${bugs.length})`, icon: '🐛' },
        { id: 'features', label: `Features (${features.length})`, icon: '✨' },
        { id: 'journal', label: `Journal (${journal.length})`, icon: '📓' },
        { id: 'ideas', label: `Ideas (${ideas.length})`, icon: '💡' },
    ];

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-4 sm:p-6">
            <div className="max-w-7xl mx-auto">
                <div className="mb-6">
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Development Tracker</h1>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Track bugs, features, journal entries, and improvement ideas</p>
                </div>

                {/* Tabs */}
                <div className="flex flex-wrap gap-2 mb-6 border-b border-gray-200 dark:border-gray-700 pb-4">
                    {tabs.map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                                activeTab === tab.id
                                    ? 'bg-blue-500 text-white'
                                    : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                            }`}
                        >
                            {tab.icon} {tab.label}
                        </button>
                    ))}
                </div>

                {/* Summary Tab */}
                {activeTab === 'summary' && summary && (
                    <div className="space-y-6">
                        {/* Stats Grid */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-4 text-center">
                                <div className="text-3xl font-bold text-red-600 dark:text-red-400">{summary.bugs_open}</div>
                                <div className="text-sm text-red-700 dark:text-red-300">Open Bugs</div>
                            </div>
                            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 text-center">
                                <div className="text-3xl font-bold text-blue-600 dark:text-blue-400">{summary.features_total}</div>
                                <div className="text-sm text-blue-700 dark:text-blue-300">Features</div>
                            </div>
                            <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4 text-center">
                                <div className="text-3xl font-bold text-purple-600 dark:text-purple-400">{summary.journal_active}</div>
                                <div className="text-sm text-purple-700 dark:text-purple-300">Journal Entries</div>
                            </div>
                            <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-4 text-center">
                                <div className="text-3xl font-bold text-yellow-600 dark:text-yellow-400">{summary.ideas_open}</div>
                                <div className="text-sm text-yellow-700 dark:text-yellow-300">Open Ideas</div>
                            </div>
                        </div>

                        {/* Critical Issues */}
                        <div className="grid md:grid-cols-2 gap-6">
                            <Card title="Critical Bugs" icon="🚨" count={summary.top_critical_bugs.length} color="bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
                                {summary.top_critical_bugs.length > 0 ? (
                                    summary.top_critical_bugs.map(bug => (
                                        <ItemRow
                                            key={bug.id}
                                            id={bug.id}
                                            title={bug.title}
                                            badges={
                                                <>
                                                    <Badge color={severityColors[bug.severity]}>{bug.severity}</Badge>
                                                    <Badge color={statusColors[bug.status]}>{bug.status}</Badge>
                                                </>
                                            }
                                            date={bug.created_at}
                                        />
                                    ))
                                ) : (
                                    <p className="text-sm text-gray-500 dark:text-gray-400 py-4 text-center">No critical bugs!</p>
                                )}
                            </Card>

                            <Card title="High Priority Ideas" icon="⭐" count={summary.top_high_ideas.length} color="bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
                                {summary.top_high_ideas.length > 0 ? (
                                    summary.top_high_ideas.map(idea => (
                                        <ItemRow
                                            key={idea.id}
                                            id={idea.id}
                                            title={idea.title}
                                            badges={
                                                <>
                                                    <Badge color={severityColors[idea.priority]}>{idea.priority}</Badge>
                                                    <Badge color={statusColors[idea.status]}>{idea.status}</Badge>
                                                </>
                                            }
                                            date={idea.created_at}
                                        />
                                    ))
                                ) : (
                                    <p className="text-sm text-gray-500 dark:text-gray-400 py-4 text-center">No high priority ideas</p>
                                )}
                            </Card>
                        </div>
                    </div>
                )}

                {/* Bugs Tab */}
                {activeTab === 'bugs' && (
                    <Card title="All Bugs" icon="🐛" count={bugs.length} color="bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
                        {bugs.map(bug => (
                            <ItemRow
                                key={bug.id}
                                id={bug.id}
                                title={bug.title}
                                description={bug.description}
                                badges={
                                    <>
                                        <Badge color={severityColors[bug.severity]}>{bug.severity}</Badge>
                                        <Badge color={statusColors[bug.status]}>{bug.status}</Badge>
                                    </>
                                }
                                date={bug.created_at}
                            />
                        ))}
                    </Card>
                )}

                {/* Features Tab */}
                {activeTab === 'features' && (
                    <Card title="All Features" icon="✨" count={features.length} color="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                        {features.map(feature => (
                            <ItemRow
                                key={feature.id}
                                id={feature.id}
                                title={feature.title}
                                description={feature.expected_behavior}
                                badges={
                                    <>
                                        <Badge color="bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200">{feature.category}</Badge>
                                        <Badge color={statusColors[feature.status]}>{feature.status}</Badge>
                                    </>
                                }
                                date={feature.created_at}
                            />
                        ))}
                    </Card>
                )}

                {/* Journal Tab */}
                {activeTab === 'journal' && (
                    <Card title="Journal Entries" icon="📓" count={journal.length} color="bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                        {journal.map(entry => (
                            <ItemRow
                                key={entry.id}
                                id={entry.id}
                                title={entry.title}
                                description={entry.content}
                                badges={
                                    <>
                                        <Badge color="bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200">{entry.entry_type}</Badge>
                                        <Badge color={statusColors[entry.status]}>{entry.status}</Badge>
                                        {entry.affects_production && <Badge color="bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">PROD</Badge>}
                                    </>
                                }
                                date={entry.created_at}
                            />
                        ))}
                    </Card>
                )}

                {/* Ideas Tab */}
                {activeTab === 'ideas' && (
                    <Card title="Improvement Ideas" icon="💡" count={ideas.length} color="bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
                        {ideas.map(idea => (
                            <ItemRow
                                key={idea.id}
                                id={idea.id}
                                title={idea.title}
                                description={idea.rationale}
                                badges={
                                    <>
                                        <Badge color={severityColors[idea.priority]}>{idea.priority}</Badge>
                                        <Badge color={statusColors[idea.status]}>{idea.status}</Badge>
                                        <Badge color="bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200">{idea.category}</Badge>
                                    </>
                                }
                                date={idea.created_at}
                            />
                        ))}
                    </Card>
                )}
            </div>
        </div>
    );
};

export default DevTrackerPage;
